import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import {
  ClipboardCheck, Package, CalendarClock, Trash2, Sparkles, Bell,
  Check, Languages, Gem,
} from "lucide-react";

const FEATURES = [
  { icon: ClipboardCheck, tKey: "land.f1t", dKey: "land.f1d", cls: "bg-violet-600" },
  { icon: Package, tKey: "land.f2t", dKey: "land.f2d", cls: "bg-emerald-600" },
  { icon: CalendarClock, tKey: "land.f3t", dKey: "land.f3d", cls: "bg-amber-600" },
  { icon: Trash2, tKey: "land.f4t", dKey: "land.f4d", cls: "bg-orange-600" },
  { icon: Sparkles, tKey: "land.f5t", dKey: "land.f5d", cls: "bg-sky-600" },
  { icon: Bell, tKey: "land.f6t", dKey: "land.f6d", cls: "bg-rose-600" },
];

const PLANS = [
  { id: "solo", name: "Solo", price: 19, feats: 3 },
  { id: "pro", name: "Pro", price: 49, feats: 4, popular: true },
  { id: "multi", name: "Multi-sede", price: 99, feats: 5 },
];

export default function LandingPage() {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="landing-page">
      <header className="max-w-6xl mx-auto px-6 h-20 flex items-center gap-3">
        <img src="/logo.png" alt="T2WORK KITCHEN" className="h-11 w-11 rounded-xl object-contain bg-white p-1" data-testid="landing-logo" />
        <span className="font-display font-extrabold text-lg">{t("appName")}</span>
        <div className="flex-1" />
        <button data-testid="landing-language-toggle" onClick={toggle}
          className="h-10 px-3 rounded-xl border border-slate-700 text-xs font-bold uppercase flex items-center gap-1.5 text-slate-300 hover:bg-slate-800 transition-colors">
          <Languages className="h-4 w-4" />{lang}
        </button>
        <button data-testid="landing-login-button" onClick={() => navigate("/login")}
          className="h-10 px-5 rounded-xl bg-white text-slate-900 text-sm font-bold hover:bg-slate-200 transition-colors">
          {t("land.ctaLogin")}
        </button>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-20 text-center" data-testid="landing-hero">
        <span className="inline-block text-xs font-bold uppercase tracking-widest text-sky-300 bg-sky-500/10 border border-sky-500/30 rounded-full px-4 py-1.5 mb-6">
          {t("land.badge")}
        </span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight max-w-3xl mx-auto">
          {t("land.heroTitle")}
        </h1>
        <p className="mt-6 text-slate-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
          {t("land.heroSub")}
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <button data-testid="landing-cta-trial" onClick={() => navigate("/login?tab=register")}
            className="h-14 px-8 rounded-xl bg-emerald-600 text-white font-bold text-base hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-600/30">
            {t("land.ctaTrial")}
          </button>
          <button data-testid="landing-cta-login" onClick={() => navigate("/login")}
            className="h-14 px-8 rounded-xl border border-slate-700 text-slate-200 font-bold text-base hover:bg-slate-800 transition-colors">
            {t("land.ctaLogin")}
          </button>
        </div>
        <p className="mt-4 text-xs text-slate-500">{t("land.noCard")}</p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20" data-testid="landing-features">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-center mb-10">{t("land.featuresTitle")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(({ icon: Icon, tKey, dKey, cls }, i) => (
            <div key={i} data-testid={`landing-feature-${i}`}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 hover:border-slate-600 transition-colors">
              <div className={`h-11 w-11 rounded-xl ${cls} flex items-center justify-center mb-4`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="font-display font-bold text-lg mb-1.5">{t(tKey)}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{t(dKey)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-20" data-testid="landing-pricing">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-center mb-10">{t("land.pricingTitle")}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {PLANS.map((p) => (
            <div key={p.id} data-testid={`landing-plan-${p.id}`}
              className={`rounded-2xl border-2 p-6 flex flex-col gap-4 relative bg-slate-900 ${p.popular ? "border-emerald-500" : "border-slate-800"}`}>
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-emerald-600 text-white">
                  {t("land.popular")}
                </span>
              )}
              <div className="flex items-center gap-2">
                <Gem className={`h-5 w-5 ${p.popular ? "text-emerald-400" : "text-slate-500"}`} />
                <h3 className="font-display font-extrabold text-xl">{p.name}</h3>
              </div>
              <div>
                <span className="font-display text-4xl font-extrabold">€{p.price}</span>
                <span className="text-sm text-slate-500">{t("land.perMonth")}</span>
              </div>
              <ul className="space-y-2 flex-1">
                {Array.from({ length: p.feats }, (_, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="h-4 w-4 text-emerald-500 shrink-0" />{t(`land.plan_${p.id}_${i}`)}
                  </li>
                ))}
              </ul>
              <button data-testid={`landing-plan-cta-${p.id}`} onClick={() => navigate("/login?tab=register")}
                className={`h-12 rounded-xl font-bold transition-colors ${p.popular ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-slate-800 hover:bg-slate-700 text-white"}`}>
                {t("land.start")}
              </button>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800 py-8 text-center" data-testid="landing-footer">
        <p className="text-xs text-slate-500">
          © 2026 {t("appName")} — {t("land.footer")}{" "}
          <a href="https://t2work.net" target="_blank" rel="noopener" className="text-sky-400 hover:underline">t2work.net</a>
        </p>
      </footer>
    </div>
  );
}
